import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import useDataviewerStore, { dataviewerActions } from '@/stores/dataviewerStore';

const DISPLAY_KEYS = ['focus', 'tech', 'proj', 'continent', 'region', 'dataset_version'];

const LABELS = { proj: 'Projection', dataset_version: 'Sesame version', focus: 'Focus' };

const FilterDropdown = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const count = selected.length;

  const toggle = opt => {
    const next = selected.includes(opt) ? selected.filter(v => v !== opt) : [...selected, opt];
    onChange(next);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={`btn btn-sm gap-1.5 ${count > 0 ? 'btn-primary' : 'btn-ghost border border-gray-300'}`}
        onClick={() => setOpen(o => !o)}
      >
        <span className="capitalize">{label}</span>
        {count > 0 && <span className="badge badge-sm badge-primary ml-0.5">{count}</span>}
        <ChevronDownIcon className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-48 max-h-64 overflow-y-auto">
          {count > 0 && (
            <>
              <button
                type="button"
                className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:bg-gray-50"
                onClick={() => onChange([])}
              >
                Clear selection
              </button>
              <div className="border-t border-gray-100 mx-2 my-1" />
            </>
          )}
          {options.map(opt => (
            <label
              key={opt}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer select-none"
            >
              <input
                type="checkbox"
                className="checkbox checkbox-xs"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

FilterDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

const DataviewerFilters = ({ filterOptions }) => {
  const filters = useDataviewerStore(s => s.filters);

  const visibleKeys = DISPLAY_KEYS.filter(k => filterOptions[k]?.length > 1);
  if (!visibleKeys.length) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {visibleKeys.map(key => (
        <FilterDropdown
          key={key}
          label={LABELS[key] ?? key}
          options={filterOptions[key]}
          selected={filters[key] ?? []}
          onChange={values => dataviewerActions.setFilter(key, values)}
        />
      ))}
    </div>
  );
};

DataviewerFilters.propTypes = {
  filterOptions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
};

export default DataviewerFilters;
